import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, input, linkedSignal, signal, viewChild } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { AlertService } from "../../../services/alert.service";
import { S25Util } from "../../../util/s25-util";
import { s25Resource } from "../../../util/s25.resource";
import { S25ButtonComponent } from "../../../standalone/s25.button.component";
import { S25DndSortableModule } from "../../s25-dnd-sortable/s25.dnd.sortable.module";
import { S25IconComponent } from "../../s25-icon/s25.icon.component";
import { TrailApi, type RequirementRootSummary, type TrackDetail, type TrackSaveInput } from "./trail.api";
import { TrailLabels } from "./trail.labels";
import {
    changedSortOrders,
    createSortOrderSaveQueue,
    nextAppendSortOrder,
    SORT_ORDER_PERSIST_DEBOUNCE_MS,
} from "../../../util/sort.order.util";
import { editorNodeToSaveTree } from "./trail.requirement.tree.component";
import { TrailRequirementTreeEditorComponent } from "./trail.requirement.tree.editor.component";

@Component({
    selector: "s25-ng-trail-curriculum-tracks",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        FormsModule,
        S25ButtonComponent,
        S25DndSortableModule,
        S25IconComponent,
        TrailRequirementTreeEditorComponent,
    ],
    host: { class: "trail-curriculum-tracks c-margin-top--double" },
    template: `
        <section class="trail-curriculum-tracks__section">
            <h3>{{ labels.TRACKS }}</h3>
            <p class="c-text--secondary">
                Distinctions such as honors or concentrations. Each {{ labels.TRACK.toLowerCase() }} may attach its own
                {{ labels.TRACK }} requirement tree.
            </p>
            @if (tracksResource.isLoading()) {
                <p>Loading…</p>
            } @else if (tracksResource.error()) {
                <p class="c-text--error">{{ formatError(tracksResource.error()) }}</p>
            } @else {
                @if (!tracks().length) {
                    <p class="c-text--secondary">No {{ labels.TRACKS.toLowerCase() }} defined yet.</p>
                } @else {
                    <table class="c-table trail-curriculum-tracks__table">
                        <thead>
                            <tr>
                                <th class="trail-curriculum-tracks__drag-col"></th>
                                <th>Name</th>
                                <th>Code</th>
                                <th>Tree</th>
                                <th>{{ labels.CANNOT_COMBINE_WITH }}</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody
                            s25-ng-dnd-sortable
                            [items]="sortableTracks()"
                            (orderChanged)="onTrackOrderChanged($event)"
                        >
                            @for (track of sortableTracks(); track track.trackId; let trackIndex = $index) {
                                <tr s25-ng-dnd-sortable-item [index]="trackIndex" [draggable]="true">
                                    <td class="trail-curriculum-tracks__drag-col">
                                        <s25-ng-icon
                                            [type]="'dragHandle'"
                                            [ariaLabel]="'Reorder ' + labels.TRACK.toLowerCase()"
                                        />
                                    </td>
                                    <td>{{ track.name }}</td>
                                    <td>{{ track.code }}</td>
                                    <td>{{ treeLabel(track.requirementTreeId) }}</td>
                                    <td>{{ cannotCombineWithLabel(track) }}</td>
                                    <td>
                                        <div class="trail-curriculum-tracks__row-actions">
                                            <s25-ng-button [type]="'outline'" [onClick]="editFactory(track)">
                                                Edit
                                            </s25-ng-button>
                                            <s25-ng-button [type]="'danger--outline'" [onClick]="disableFactory(track)">
                                                Disable
                                            </s25-ng-button>
                                        </div>
                                    </td>
                                </tr>
                            }
                        </tbody>
                    </table>
                }
                <p class="c-margin-top--single">
                    <s25-ng-button [type]="'outline'" [onClick]="startCreate"> Add {{ labels.TRACK }} </s25-ng-button>
                </p>
            }

            @if (showForm()) {
                <form
                    class="trail-curriculum-tracks__form c-margin-top--single"
                    [class.trail-curriculum-tracks__form--with-tree]="inlineTreeId() != null"
                    (ngSubmit)="$event.preventDefault(); saveTrack()"
                >
                    <h4>{{ editingTrackId() ? "Edit" : "New" }} {{ labels.TRACK }}</h4>
                    <div class="form-group c-margin-bottom--half">
                        <label>Name *</label>
                        <input
                            class="c-input"
                            [ngModel]="formName()"
                            (ngModelChange)="formName.set($event)"
                            name="trackName"
                            required
                        />
                    </div>
                    <div class="form-group c-margin-bottom--half">
                        <label>Code *</label>
                        <input
                            class="c-input"
                            [ngModel]="formCode()"
                            (ngModelChange)="formCode.set($event)"
                            name="trackCode"
                            required
                        />
                        @if (codeConflict()) {
                            <p class="c-text--error c-margin-top--half">
                                An active {{ labels.TRACK.toLowerCase() }} with this code already exists on this
                                curriculum.
                            </p>
                        }
                    </div>
                    <div class="form-group c-margin-bottom--half">
                        <label>Decoration</label>
                        <textarea
                            class="c-input"
                            rows="2"
                            [ngModel]="formDecoration()"
                            (ngModelChange)="formDecoration.set($event)"
                            name="trackDecoration"
                            placeholder="Student-facing extra copy"
                        ></textarea>
                    </div>
                    <div class="form-group c-margin-bottom--half">
                        <label>{{ labels.TRACK }} tree</label>
                        <select
                            class="c-input"
                            [ngModel]="formTreeId()"
                            (ngModelChange)="onTreeSelect($event)"
                            name="trackTree"
                            [disabled]="showCreateTree()"
                        >
                            <option [ngValue]="null">None</option>
                            @for (root of trackRoots(); track root.requirementGroupId) {
                                <option [ngValue]="root.requirementGroupId">
                                    {{ root.name }}{{ root.code ? " (" + root.code + ")" : "" }}
                                </option>
                            }
                        </select>
                    </div>
                    @if (!showCreateTree()) {
                        <s25-ng-button [type]="'outline'" [onClick]="openCreateTree">Create tree</s25-ng-button>
                    } @else {
                        <div class="trail-curriculum-tracks__create-tree c-margin-top--half">
                            <div class="form-group c-margin-bottom--half">
                                <label>Tree name *</label>
                                <input
                                    class="c-input"
                                    [ngModel]="createTreeName()"
                                    (ngModelChange)="createTreeName.set($event)"
                                    name="createTreeName"
                                />
                            </div>
                            <div class="form-group c-margin-bottom--half">
                                <label>Tree code *</label>
                                <input
                                    class="c-input"
                                    [ngModel]="createTreeCode()"
                                    (ngModelChange)="createTreeCode.set($event)"
                                    name="createTreeCode"
                                />
                            </div>
                            <s25-ng-button [type]="'danger--outline'" [onClick]="cancelCreateTree"
                                >Cancel create tree</s25-ng-button
                            >
                        </div>
                    }
                    @if (inlineTreeId() != null) {
                        <details class="trail-curriculum-tracks__tree-section c-margin-top--single" open>
                            <summary>{{ inlineTreeTitle() }}</summary>
                            <s25-ng-trail-requirement-tree-editor
                                [cohortId]="cohortId()"
                                [rootGroupId]="inlineTreeId()"
                                [showStatus]="true"
                                [domain]="'COCURRICULAR'"
                            />
                        </details>
                    }
                    @if (siblingTracks().length) {
                        <fieldset class="trail-curriculum-tracks__exclusivity c-margin-top--single">
                            <legend>{{ labels.CANNOT_COMBINE_WITH }}</legend>
                            @for (sibling of siblingTracks(); track sibling.trackId) {
                                <label class="trail-curriculum-tracks__exclusive-option">
                                    <input
                                        type="checkbox"
                                        [checked]="isExclusive(sibling.trackId)"
                                        (change)="onExclusiveChange(sibling.trackId, $event)"
                                    />
                                    {{ sibling.name }}
                                </label>
                            }
                        </fieldset>
                    }
                    <div class="trail-curriculum-tracks__form-actions c-margin-top--single">
                        <s25-ng-button [type]="'primary'" [onClick]="saveTrack" [disabled]="!canSave() || saving()">
                            {{ saving() ? "Saving…" : "Save " + labels.TRACK }}
                        </s25-ng-button>
                        <s25-ng-button [type]="'danger--outline'" [onClick]="cancelForm">Cancel</s25-ng-button>
                    </div>
                </form>
            }
        </section>
    `,
    styles: `
        :host {
            display: block;
        }
        .trail-curriculum-tracks__table {
            width: 100%;
            border-collapse: collapse;
        }
        .trail-curriculum-tracks__table th,
        .trail-curriculum-tracks__table td {
            border: 1px solid var(--color--border, #ddd);
            padding: 0.5rem 0.75rem;
            text-align: left;
        }
        .trail-curriculum-tracks__row-actions {
            display: flex;
            gap: 0.5rem;
            flex-wrap: wrap;
        }
        .trail-curriculum-tracks__drag-col {
            width: 1.75rem;
            text-align: center;
        }
        .trail-curriculum-tracks__table tbody.sortable [s25-ng-dnd-sortable-item] {
            cursor: grab;
        }
        .trail-curriculum-tracks__form {
            border: 1px solid var(--color--border, #ddd);
            border-radius: 6px;
            padding: 1rem;
            max-width: 40rem;
        }
        .trail-curriculum-tracks__form--with-tree {
            max-width: min(100%, 64rem);
        }
        .trail-curriculum-tracks__tree-section {
            border: 1px solid var(--color--border, #ddd);
            border-radius: 6px;
            padding: 0.75rem 1rem;
        }
        .trail-curriculum-tracks__tree-section summary {
            cursor: pointer;
            font-weight: 600;
        }
        .trail-curriculum-tracks__form .form-group {
            display: grid;
            grid-template-columns: 10rem minmax(0, 1fr);
            gap: 0.75rem;
            align-items: start;
        }
        .trail-curriculum-tracks__exclusivity {
            border: none;
            padding: 0;
            display: flex;
            flex-direction: column;
            gap: 0.35rem;
        }
        .trail-curriculum-tracks__exclusive-option {
            display: flex;
            align-items: center;
            gap: 0.5rem;
        }
        .trail-curriculum-tracks__form-actions {
            display: flex;
            gap: 0.75rem;
            flex-wrap: wrap;
        }
    `,
})
export class TrailCurriculumTracksComponent {
    readonly defnId = input.required<number>();
    readonly cohortId = input.required<number>();

    readonly labels = TrailLabels;

    private readonly reloadGen = signal(0);
    readonly showForm = signal(false);
    readonly editingTrackId = signal<number | null>(null);
    readonly saving = signal(false);
    readonly showCreateTree = signal(false);
    readonly createTreeName = signal("");
    readonly createTreeCode = signal("");

    readonly tracksResource = s25Resource({
        params: () => ({ defnId: this.defnId(), gen: this.reloadGen() }),
        loader: ({ params }) => TrailApi.listTracks(params.defnId),
    });

    readonly trackRootsResource = s25Resource({
        params: () => ({ cohortId: this.cohortId(), gen: this.reloadGen() }),
        loader: ({ params }) => TrailApi.listRequirementRoots(params.cohortId, "TRACK"),
    });

    readonly tracks = computed((): TrackDetail[] => this.tracksResource.value() ?? []);
    readonly trackRoots = computed((): RequirementRootSummary[] => this.trackRootsResource.value() ?? []);
    readonly treeEditor = viewChild(TrailRequirementTreeEditorComponent);

    readonly inlineTreeId = computed((): number | null => {
        if (!this.showForm() || this.showCreateTree()) {
            return null;
        }
        return this.formTreeId();
    });

    readonly inlineTreeTitle = computed(() => {
        const treeId = this.inlineTreeId();
        if (treeId == null) {
            return `Edit ${this.labels.TRACK} tree`;
        }
        return this.treeLabel(treeId);
    });

    readonly sortedTracks = computed((): TrackDetail[] => {
        const list = this.tracks();
        return [...list].sort((trackA, trackB) => {
            const orderA = trackA.sortOrder ?? Number.MAX_SAFE_INTEGER;
            const orderB = trackB.sortOrder ?? Number.MAX_SAFE_INTEGER;
            if (orderA !== orderB) return orderA - orderB;
            return (trackA.name ?? "").localeCompare(trackB.name ?? "", undefined, { sensitivity: "base" });
        });
    });

    readonly sortableTracks = linkedSignal((): TrackDetail[] => this.sortedTracks());
    private trackOrderPersistHandle: ReturnType<typeof setTimeout> | null = null;
    private readonly enqueueTrackOrderSave = createSortOrderSaveQueue();

    readonly selectedTrack = computed((): TrackDetail | null => {
        const trackId = this.editingTrackId();
        if (trackId == null) {
            return null;
        }
        return this.tracks().find((track) => track.trackId === trackId) ?? null;
    });

    readonly formName = linkedSignal(() => this.selectedTrack()?.name ?? "");
    readonly formCode = linkedSignal(() => this.selectedTrack()?.code ?? "");
    readonly formDecoration = linkedSignal(() => this.selectedTrack()?.decoration ?? "");
    readonly formTreeId = linkedSignal<number | null>(() => this.selectedTrack()?.requirementTreeId ?? null);
    readonly exclusiveIds = linkedSignal({
        source: () => this.editingTrackId(),
        computation: (trackId) => {
            if (trackId == null) {
                return [] as number[];
            }
            const track = this.tracks().find((item) => item.trackId === trackId);
            return (track?.cannotCombineWith ?? []).map((item) => item.itemId);
        },
    });

    readonly siblingTracks = computed((): TrackDetail[] => {
        const editingId = this.editingTrackId();
        return this.tracks().filter((track) => track.trackId !== editingId);
    });

    readonly codeConflict = computed(() => {
        const excludeId = this.editingTrackId();
        const existingCodes = this.tracks()
            .filter((track) => track.trackId !== excludeId)
            .map((track) => track.code);
        return TrailApi.isTrailCodeTaken(this.formCode(), existingCodes);
    });

    formatError(error: unknown): string {
        if (error instanceof Error) return error.message;
        return String(error ?? "Unknown error");
    }

    cannotCombineWithLabel(track: TrackDetail): string {
        const names = (track.cannotCombineWith ?? []).map((item) => item.itemName).filter((itemName) => !!itemName);
        return names.length ? names.join(", ") : "—";
    }

    treeLabel(requirementTreeId?: number): string {
        if (requirementTreeId == null) {
            return "—";
        }
        const root = this.trackRoots().find((item) => item.requirementGroupId === requirementTreeId);
        return root ? `${root.name}${root.code ? " (" + root.code + ")" : ""}` : "Attached";
    }

    isExclusive(trackId: number): boolean {
        return this.exclusiveIds().includes(trackId);
    }

    toggleExclusive(trackId: number, checked: boolean) {
        const current = this.exclusiveIds();
        if (checked) {
            if (!current.includes(trackId)) {
                this.exclusiveIds.set([...current, trackId]);
            }
            return;
        }
        this.exclusiveIds.set(current.filter((id) => id !== trackId));
    }

    onExclusiveChange(trackId: number, event: Event) {
        const checkbox = event.target as HTMLInputElement;
        this.toggleExclusive(trackId, checkbox.checked);
    }

    canSave(): boolean {
        return !!this.formName().trim() && !!this.formCode().trim() && !this.codeConflict() && this.createTreeValid();
    }

    startCreate = () => {
        this.editingTrackId.set(null);
        this.showCreateTree.set(false);
        this.createTreeName.set("");
        this.createTreeCode.set("");
        this.showForm.set(true);
    };

    editFactory = (track: TrackDetail) => () => this.editTrack(track);

    editTrack(track: TrackDetail) {
        this.editingTrackId.set(track.trackId);
        this.showCreateTree.set(false);
        this.createTreeName.set("");
        this.createTreeCode.set("");
        this.showForm.set(true);
    }

    disableFactory = (track: TrackDetail) => () => this.disableTrack(track);

    cancelForm = () => {
        this.showForm.set(false);
        this.editingTrackId.set(null);
        this.showCreateTree.set(false);
    };

    openCreateTree = () => {
        this.showCreateTree.set(true);
        this.formTreeId.set(null);
    };

    cancelCreateTree = () => {
        this.showCreateTree.set(false);
        this.createTreeName.set("");
        this.createTreeCode.set("");
    };

    onTreeSelect(treeId: number | null) {
        this.formTreeId.set(treeId);
        if (treeId != null) {
            this.showCreateTree.set(false);
        }
    }

    saveTrack = async () => {
        if (!this.canSave() || this.saving()) {
            return;
        }
        this.saving.set(true);
        const createdNewTree = this.showCreateTree();
        try {
            let requirementTreeId = this.formTreeId();
            if (createdNewTree) {
                const created = await TrailApi.createRequirementRoot(this.cohortId(), {
                    kind: "TRACK",
                    name: this.createTreeName().trim(),
                    code: this.createTreeCode().trim(),
                });
                requirementTreeId = created.rootGroupId;
            }
            const treeEditor = this.treeEditor();
            const tree = treeEditor?.tree() ?? null;
            if (requirementTreeId != null && tree != null) {
                if (treeEditor?.isPublished()) {
                    const confirmed = await AlertService.confirm(
                        "This tree is published. Saving will affect active students. This is not a retroactive change. Continue?",
                    );
                    if (!confirmed) {
                        return;
                    }
                }
                await TrailApi.saveRequirementRoot(requirementTreeId, editorNodeToSaveTree(tree, this.cohortId()));
            }
            const payload: TrackSaveInput = {
                trackId: this.editingTrackId() ?? undefined,
                name: this.formName().trim(),
                code: this.formCode().trim(),
                decoration: this.formDecoration().trim() || undefined,
                requirementTreeId: requirementTreeId ?? undefined,
                sortOrder: this.selectedTrack()?.sortOrder ?? this.nextSortOrder(),
            };
            const saved = await TrailApi.saveTrack(this.defnId(), payload);
            const trackId = saved.trackId ?? this.editingTrackId();
            if (trackId != null) {
                await TrailApi.replaceTrackExclusivity(this.defnId(), trackId, this.exclusiveIds());
            }
            this.showCreateTree.set(false);
            this.reloadGen.update((count) => count + 1);
            if (createdNewTree && trackId != null && requirementTreeId != null) {
                this.formTreeId.set(requirementTreeId);
                this.editingTrackId.set(trackId);
                this.showForm.set(true);
            } else {
                this.showForm.set(false);
                this.editingTrackId.set(null);
            }
        } catch (caughtError) {
            await AlertService.alert(S25Util.errorText(caughtError));
        } finally {
            this.saving.set(false);
        }
    };

    onTrackOrderChanged(tracks: TrackDetail[]): void {
        this.sortableTracks.set(tracks);
        this.queueTrackOrderPersist();
    }

    private queueTrackOrderPersist(): void {
        if (this.trackOrderPersistHandle != null) {
            clearTimeout(this.trackOrderPersistHandle);
        }
        this.trackOrderPersistHandle = setTimeout(() => {
            this.trackOrderPersistHandle = null;
            void this.enqueueTrackOrderSave(() => this.persistTrackOrder());
        }, SORT_ORDER_PERSIST_DEBOUNCE_MS);
    }

    private async persistTrackOrder(): Promise<void> {
        const tracks = this.sortableTracks();
        const { orders, changes } = changedSortOrders(
            tracks,
            (track) => track.trackId,
            (track) => track.sortOrder,
        );
        if (!changes.length) {
            return;
        }
        const updated = tracks.map((track, trackIndex) => ({ ...track, sortOrder: orders[trackIndex] }));
        this.sortableTracks.set(updated);
        try {
            await TrailApi.updateTrackSortOrders(
                this.defnId(),
                changes.map((change) => ({ trackId: change.id, sortOrder: change.sortOrder })),
            );
        } catch (caughtError) {
            if (this.sortableTracks() === updated) {
                this.sortableTracks.set(tracks);
            }
            await AlertService.alert(S25Util.errorText(caughtError));
        }
    }

    private async disableTrack(track: TrackDetail) {
        const confirmed = await AlertService.confirm(
            `Remove ${TrailLabels.TRACK.toLowerCase()} "${track.name}"? It will no longer appear on this curriculum. This is a permanent action.`,
        );
        if (!confirmed) {
            return;
        }
        try {
            await TrailApi.disableTrack(this.defnId(), track.trackId);
            if (this.editingTrackId() === track.trackId) {
                this.cancelForm();
            }
            this.reloadGen.update((count) => count + 1);
        } catch (caughtError) {
            await AlertService.alert(S25Util.errorText(caughtError));
        }
    }

    private nextSortOrder(): number {
        return nextAppendSortOrder(this.sortableTracks().map((track) => track.sortOrder));
    }

    private createTreeValid(): boolean {
        if (!this.showCreateTree()) {
            return true;
        }
        return !!this.createTreeName().trim() && !!this.createTreeCode().trim();
    }
}
